// BugzillaProxyController.js
import axios from "axios";

// Get token and user context from localStorage
const getAuthToken = () => localStorage.getItem('access-token');
const getUserId = () => localStorage.getItem('user_id');
const getCompanyId = () => localStorage.getItem('company_id');
const getUserEmail = () => localStorage.getItem('user_email');

//  UPDATED: Use the correct API base URL with /bugzilla-api
const API_BASE_URL = process.env.REACT_APP_BUGZILLA_BACKEND_URL || 'https://www.floxpad.com/bugzilla-api';

// Create axios instance for JSON requests
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
  timeout: 30000,
});

//  FIXED: Request interceptor - Properly handles authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const userId = getUserId();
    const companyId = getCompanyId();
    const userEmail = getUserEmail();
    
    // Initialize params if not exists
    config.params = config.params || {};
    
    // Add user_email to params (backend expects this for all requests)
    if (userEmail) {
      config.params.user_email = userEmail;
    }
    
    // Add other optional params
    if (userId) {
      config.params.user_id = userId;
    }
    if (companyId) {
      config.params.company_id = companyId;
    }
    
    console.log(`API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);
    console.log('Params:', config.params);
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.url}`, response.status);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      console.error("Authentication error - Token expired");
      localStorage.removeItem('access-token');
      localStorage.removeItem('user_id');
      localStorage.removeItem('company_id');
      localStorage.removeItem('user_email');
      window.location.href = '/login';
    } else if (error.response?.status === 404) {
      console.error("API endpoint not found:", error.config?.url);
      console.error("Full URL:", error.config?.baseURL + error.config?.url);
    } else if (error.code === 'ECONNREFUSED') {
      console.error("Cannot connect to backend on", API_BASE_URL);
    }
    
    console.error("API Error:", error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============ HELPER FUNCTIONS ============

// Convert file to base64
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:image/png;base64, prefix
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

// Convert base64 to blob
export const base64ToBlob = (base64, contentType = 'application/octet-stream') => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  
  return new Blob(byteArrays, { type: contentType });
};

// ============ INTEGRATION APIs ============

/**
 * Install/Configure Bugzilla integration
 */
export const installBugzillaIntegration_API = async (config) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.post(
      '/integrations/install',
      {
        bugzilla_url: config.bugzilla_url,
        api_key: config.api_key,
        type: config.type || 'bugzilla',
        is_enabled: config.is_enabled !== undefined ? config.is_enabled : true,
        is_shared: config.is_shared || false,
        settings: config.settings || {},
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Install integration error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get Bugzilla integration status
 */
export const getBugzillaStatus_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(
      '/integrations/bugzilla/status',
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Get status error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * List all integrations
 */
export const listIntegrations_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(
      '/integrations/list',
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("List integrations error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update integration status (enable/disable)
 */
export const updateIntegrationStatus_API = async (integrationId, isEnabled) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.patch(
      `/integrations/${integrationId}/status`,
      {
        is_enabled: isEnabled,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Update integration status error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete integration
 */
export const deleteIntegration_API = async (integrationId) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.delete(
      `/integrations/${integrationId}`,
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Delete integration error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ ATTACHMENT APIs ============

/**
 * Upload attachment using multipart/form-data (Recommended for files)
 */
export const uploadAttachmentMultipart_API = async (bugId, file, options = {}) => {
  try {
    const token = getAuthToken();
    const userEmail = getUserEmail();
    
    if (!token || !userEmail) {
      throw new Error('Authentication required');
    }
    
    const formData = new FormData();
    formData.append('data', file);
    formData.append('file_name', options.file_name || file.name);
    formData.append('summary', options.summary || file.name);
    formData.append('content_type', options.content_type || file.type || 'application/octet-stream');
    formData.append('comment', options.comment || '');
    formData.append('is_patch', options.is_patch ? 'true' : 'false');
    formData.append('is_private', options.is_private ? 'true' : 'false');
    
    const response = await apiClient.post(
      `/bugzilla-proxy/bugs/${bugId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: { user_email: userEmail }
      }
    );
    
    console.log('Attachment uploaded successfully:', response.data);
    return response.data;
    
  } catch (error) {
    console.error("Upload attachment multipart error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Upload attachment using base64 in JSON (Alternative approach)
 */
export const uploadAttachmentBase64_API = async (bugId, file, options = {}) => {
  try {
    const base64Data = await fileToBase64(file);
    const userEmail = getUserEmail();
    
    const payload = {
      data: base64Data,
      file_name: options.file_name || file.name,
      summary: options.summary || file.name,
      content_type: options.content_type || file.type || 'application/octet-stream',
      comment: options.comment || '',
      is_patch: options.is_patch || false,
      is_private: options.is_private || false,
      user_email: userEmail
    };
    
    const response = await apiClient.post(
      `/bugzilla-proxy/bugs/${bugId}/attachments`,
      payload,
      {
        params: { user_email: userEmail }
      }
    );
    
    console.log('Attachment uploaded via base64:', response.data);
    return response.data;
    
  } catch (error) {
    console.error("Upload attachment base64 error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Smart attachment upload - automatically chooses best method based on file size
 */
export const uploadAttachment = async (bugId, file, options = {}) => {
  const MAX_BASE64_SIZE = 1 * 1024 * 1024; // 1MB threshold
  
  if (file.size > MAX_BASE64_SIZE || options.forceMultipart) {
    console.log(`Using multipart upload for ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
    return await uploadAttachmentMultipart_API(bugId, file, options);
  } else {
    console.log(`Using base64 upload for ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
    return await uploadAttachmentBase64_API(bugId, file, options);
  }
};

/**
 * Get all attachments for a bug
 */
export const getBugAttachments_API = async (bugId) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(
      `/bugzilla-proxy/bugs/${bugId}/attachments`,
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Get attachments error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ BUG CRUD OPERATIONS ============

export const createBugzillaBug_API = async (payload) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.post(
      "/bugzilla-proxy/bugs",
      {
        ...payload,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Create bug error:", error.response?.data || error.message);
    throw error;
  }
};

export const getBugzillaBugs_API = async (params = {}) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/bugs", { 
      params: { 
        ...params, 
        user_email: userEmail 
      } 
    });
    return response.data;
  } catch (error) {
    console.error("Get bugs error:", error.response?.data || error.message);
    throw error;
  }
};

export const getBugzillaBugById_API = async (id) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(`/bugzilla-proxy/bugs/${id}`, {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get bug by ID error:", error.response?.data || error.message);
    throw error;
  }
};

export const updateBugzillaBug_API = async (id, payload) => {
  try {
    const userEmail = getUserEmail();
    const updatePayload = {
      ...payload,
      user_email: userEmail
    };
    
    console.log(`Updating bug ${id} with payload:`, JSON.stringify(updatePayload, null, 2));
    
    const response = await apiClient.put(
      `/bugzilla-proxy/bugs/${id}`,
      updatePayload,
      {
        params: { user_email: userEmail }
      }
    );
    
    console.log("Update bug response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Update bug error:", error.response?.data || error.message);
    throw error;
  }
};

export const deleteBugzillaBug_API = async (id, resolution = 'FIXED') => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.delete(
      `/bugzilla-proxy/bugs/${id}`,
      {
        params: { 
          user_email: userEmail,
          resolution: resolution
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Delete bug error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ SEARCH APIs ============

export const searchBugs_API = async (searchParams) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/bugs/search", { 
      params: { 
        ...searchParams, 
        user_email: userEmail 
      } 
    });
    return response.data;
  } catch (error) {
    console.error("Search bugs error:", error.response?.data || error.message);
    throw error;
  }
};

export const getBugsByStatus_API = async (status, params = {}) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/bugs", { 
      params: { 
        ...params, 
        status,
        user_email: userEmail 
      } 
    });
    return response.data;
  } catch (error) {
    console.error("Get bugs by status error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ COMMENT APIs ============

export const getBugComments_API = async (bugId) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(`/bugzilla-proxy/bugs/${bugId}/comments`, {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get comments error:", error.response?.data || error.message);
    throw error;
  }
};

export const addBugComment_API = async (bugId, comment, isPrivate = false) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.post(
      `/bugzilla-proxy/bugs/${bugId}/comments`,
      {
        comment,
        is_private: isPrivate,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Add comment error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ STATUS MANAGEMENT APIs ============

export const updateBugStatus_API = async (bugId, status, resolution = null) => {
  try {
    const userEmail = getUserEmail();
    const payload = { 
      status,
      user_email: userEmail
    };
    if (resolution && (status === 'RESOLVED' || status === 'CLOSED')) {
      payload.resolution = resolution;
    }
    const response = await apiClient.put(
      `/bugzilla-proxy/bugs/${bugId}`,
      payload,
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Update status error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ ASSIGNMENT APIs ============

export const assignBug_API = async (bugId, assigneeEmail) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.put(
      `/bugzilla-proxy/bugs/${bugId}`,
      {
        assigned_to: assigneeEmail,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Assign bug error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ USER SEARCH APIs ============

export const searchUsers_API = async (searchTerm, limit = 20) => {
  try {
    if (!searchTerm || searchTerm.length < 2) {
      return { success: true, users: [] };
    }
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/users/search", { 
      params: { 
        match: searchTerm, 
        limit,
        user_email: userEmail 
      } 
    });
    return response.data;
  } catch (error) {
    console.error("Search users error:", error.response?.data || error.message);
    return { success: false, users: [], error: error.message };
  }
};

// ============ FIELD VALUES APIs ============

// ============ FIELD VALUES APIs ============

export const getBugzillaPriorities_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/priorities", {  // ← Changed from /fields/priority
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.warn("Failed to fetch priorities from API, using defaults");
    return { 
      success: false, 
      data: { 
        values: ["Highest", "High", "Normal", "Low", "Lowest"] 
      } 
    };
  }
};

export const getBugzillaSeverities_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/severities", {  // ← Changed from /fields/severity
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.warn("Failed to fetch severities from API, using defaults");
    return { 
      success: false, 
      data: { 
        values: ["blocker", "critical", "major", "normal", "minor", "trivial", "enhancement"] 
      } 
    };
  }
};

export const getBugzillaStatuses_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/statuses", {  // ← Changed from /fields/status
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.warn("Failed to fetch statuses from API, using defaults");
    return { 
      success: false, 
      data: { 
        values: ["NEW", "ASSIGNED", "RESOLVED", "VERIFIED", "CLOSED", "REOPENED"] 
      } 
    };
  }
};

export const getBugzillaOperatingSystems_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/fields/op_sys", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.warn("Failed to fetch operating systems from API, using defaults");
    return { 
      success: false, 
      data: { 
        values: ["All", "Windows", "Linux", "Mac OS", "iOS", "Android"] 
      } 
    };
  }
};

export const getBugzillaPlatforms_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/fields/platform", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.warn("Failed to fetch platforms from API, using defaults");
    return { 
      success: false, 
      data: { 
        values: ["All", "Desktop", "Mobile", "Tablet"] 
      } 
    };
  }
};

export const getBugzillaResolutions_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/resolutions", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.warn("Failed to fetch resolutions from API, using defaults");
    return { 
      success: false, 
      data: { 
        values: ["FIXED", "INVALID", "WONTFIX", "DUPLICATE", "WORKSFORME", "MOVED"] 
      } 
    };
  }
};


// ============ PRODUCT CRUD APIs ============

/**
 * Create a new product in Bugzilla
 */
export const createBugzillaProduct_API = async (productData) => {
  try {
    const userEmail = getUserEmail();
    
    // Validate required fields
    const requiredFields = ['name', 'description', 'version', 'default_milestone'];
    for (const field of requiredFields) {
      if (!productData[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }
    
    const response = await apiClient.post(
      '/bugzilla-proxy/products',
      {
        ...productData,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Create product error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get all products
 */
export const getBugzillaProducts_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/products", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get products error:", error.response?.data || error.message);
    return { success: false, data: { products: [] }, error: error.message };
  }
};

/**
 * Get a single product by name
 */
export const getBugzillaProductByName_API = async (productName) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(`/bugzilla-proxy/products/${productName}`, {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get product by name error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get enterable products (where user can create bugs)
 */
export const getEnterableProducts_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/products/enterable", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get enterable products error:", error.response?.data || error.message);
    return { success: false, data: { products: [] }, error: error.message };
  }
};

/**
 * Update a product
 */
export const updateBugzillaProduct_API = async (productName, updateData) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.put(
      `/bugzilla-proxy/products/${productName}`,
      {
        ...updateData,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Update product error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete/Deactivate a product
 */
export const deleteBugzillaProduct_API = async (productName) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.delete(
      `/bugzilla-proxy/products/${productName}`,
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Delete product error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ COMPONENT CRUD APIs ============

/**
 * Create a new component in a product
 */
export const createBugzillaComponent_API = async (componentData) => {
  try {
    const userEmail = getUserEmail();
    
    // Validate required fields
    if (!componentData.product_name || !componentData.name) {
      throw new Error('Missing required fields: product_name, name');
    }
    
    const response = await apiClient.post(
      '/bugzilla-proxy/components',
      {
        ...componentData,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Create component error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get components for a product
 */
export const getBugzillaComponents_API = async (productName) => {
  if (!productName) {
    return { success: true, data: { components: [] } };
  }
  
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/components", {
      params: { 
        productName,
        user_email: userEmail 
      }
    });
    return response.data;
  } catch (error) {
    console.error("Get components error:", error.response?.data || error.message);
    return { success: false, data: { components: [] }, error: error.message };
  }
};

/**
 * Get components by product ID
 */
export const getBugzillaComponentsByProductId_API = async (productId) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(`/bugzilla-proxy/components/by-product/${productId}`, {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get components by product ID error:", error.response?.data || error.message);
    return { success: false, data: { components: [] }, error: error.message };
  }
};

/**
 * Get a single component by ID
 */
export const getBugzillaComponentById_API = async (componentId) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(`/bugzilla-proxy/components/${componentId}`, {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get component by ID error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update a component
 */
export const updateBugzillaComponent_API = async (componentId, updateData) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.put(
      `/bugzilla-proxy/components/${componentId}`,
      {
        ...updateData,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Update component error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete/Deactivate a component
 */
export const deleteBugzillaComponent_API = async (componentId) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.delete(
      `/bugzilla-proxy/components/${componentId}`,
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Delete component error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ VERSION APIs ============

/**
 * Get versions for a product
 */
export const getBugzillaVersions_API = async (productName) => {
  if (!productName) {
    return { success: true, data: { versions: [] } };
  }
  
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/versions", {
      params: { 
        productName,
        user_email: userEmail 
      }
    });
    return response.data;
  } catch (error) {
    console.error("Get versions error:", error.response?.data || error.message);
    return { success: false, data: { versions: [] }, error: error.message };
  }
};

/**
 * Create a new version for a product
 */
export const createBugzillaVersion_API = async (versionData) => {
  try {
    const userEmail = getUserEmail();
    
    if (!versionData.product_name || !versionData.name) {
      throw new Error('Missing required fields: product_name, name');
    }
    
    const response = await apiClient.post(
      '/bugzilla-proxy/versions',
      {
        ...versionData,
        user_email: userEmail
      },
      {
        params: { user_email: userEmail }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Create version error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ MILESTONE APIs ============

/**
 * Get milestones for a product
 */
export const getBugzillaMilestones_API = async (productName) => {
  if (!productName) {
    return { success: true, data: { milestones: [] } };
  }
  
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/milestones", {
      params: { 
        productName,
        user_email: userEmail 
      }
    });
    return response.data;
  } catch (error) {
    console.error("Get milestones error:", error.response?.data || error.message);
    return { success: false, data: { milestones: [] }, error: error.message };
  }
};

// ============ USER & TEAM APIs ============

export const getCurrentUser_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/user/current", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get current user error:", error.response?.data || error.message);
    throw error;
  }
};

export const getBugzillaUsers_API = async (params = {}) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/users", { 
      params: { 
        ...params, 
        user_email: userEmail 
      } 
    });
    return response.data;
  } catch (error) {
    console.error("Get users error:", error.response?.data || error.message);
    return { success: false, users: [], error: error.message };
  }
};

// ============ TEST CONNECTION ============

export const testBugzillaConnection_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/test-connection", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Connection failed:", error.response?.data || error.message);
    throw error;
  }
};

// ============ BULK OPERATIONS APIs ============

export const bulkUpdateStatus_API = async (bugIds, status, resolution = null) => {
  try {
    const userEmail = getUserEmail();
    const promises = bugIds.map(bugId => updateBugStatus_API(bugId, status, resolution));
    const results = await Promise.allSettled(promises);
    
    const success = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    
    return {
      success: true,
      total: bugIds.length,
      updated: success,
      failed: failed,
      user_email: userEmail
    };
  } catch (error) {
    console.error("Bulk update error:", error);
    throw error;
  }
};

export const bulkAssign_API = async (bugIds, assigneeEmail) => {
  try {
    const userEmail = getUserEmail();
    const promises = bugIds.map(bugId => assignBug_API(bugId, assigneeEmail));
    const results = await Promise.allSettled(promises);
    
    const success = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    
    return {
      success: true,
      total: bugIds.length,
      updated: success,
      failed: failed,
      user_email: userEmail
    };
  } catch (error) {
    console.error("Bulk assign error:", error);
    throw error;
  }
};

// ============ DEBUG APIs ============

/**
 * Get complete debug info
 */
export const getDebugInfo_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/debug/complete", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get debug info error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get debug products only
 */
export const getDebugProducts_API = async () => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get("/bugzilla-proxy/debug/products", {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get debug products error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get debug components for a product
 */
export const getDebugComponents_API = async (productName) => {
  try {
    const userEmail = getUserEmail();
    const response = await apiClient.get(`/bugzilla-proxy/debug/components/${productName}`, {
      params: { user_email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error("Get debug components error:", error.response?.data || error.message);
    throw error;
  }
};

// ============ EXPORT ALL FUNCTIONS ============
const BugzillaProxyController = {
  // Integration APIs
  installBugzillaIntegration: installBugzillaIntegration_API,
  getBugzillaStatus: getBugzillaStatus_API,
  listIntegrations: listIntegrations_API,
  updateIntegrationStatus: updateIntegrationStatus_API,
  deleteIntegration: deleteIntegration_API,
  
  // Attachment APIs
  uploadAttachment,
  uploadAttachmentMultipart: uploadAttachmentMultipart_API,
  uploadAttachmentBase64: uploadAttachmentBase64_API,
  getAttachments: getBugAttachments_API,
  fileToBase64,
  base64ToBlob,
  
  // Bug CRUD
  createBug: createBugzillaBug_API,
  getBugs: getBugzillaBugs_API,
  getBugById: getBugzillaBugById_API,
  updateBug: updateBugzillaBug_API,
  deleteBug: deleteBugzillaBug_API,
  
  // Search
  searchBugs: searchBugs_API,
  getBugsByStatus: getBugsByStatus_API,
  
  // Comments
  getBugComments: getBugComments_API,
  addBugComment: addBugComment_API,
  
  // Status Management
  updateBugStatus: updateBugStatus_API,
  
  // Assignment
  assignBug: assignBug_API,
  
  // User Search
  searchUsers: searchUsers_API,
  
  // Bulk Operations
  bulkUpdateStatus: bulkUpdateStatus_API,
  bulkAssign: bulkAssign_API,
  
  // Field values
  getPriorities: getBugzillaPriorities_API,
  getSeverities: getBugzillaSeverities_API,
  getResolutions: getBugzillaResolutions_API,
  getStatuses: getBugzillaStatuses_API,
  getOperatingSystems: getBugzillaOperatingSystems_API,
  getPlatforms: getBugzillaPlatforms_API,
  
  // Product CRUD
  createProduct: createBugzillaProduct_API,
  getProducts: getBugzillaProducts_API,
  getProductByName: getBugzillaProductByName_API,
  getEnterableProducts: getEnterableProducts_API,
  updateProduct: updateBugzillaProduct_API,
  deleteProduct: deleteBugzillaProduct_API,
  
  // Component CRUD
  createComponent: createBugzillaComponent_API,
  getComponents: getBugzillaComponents_API,
  getComponentsByProductId: getBugzillaComponentsByProductId_API,
  getComponentById: getBugzillaComponentById_API,
  updateComponent: updateBugzillaComponent_API,
  deleteComponent: deleteBugzillaComponent_API,
  
  // Version APIs
  getVersions: getBugzillaVersions_API,
  createVersion: createBugzillaVersion_API,
  
  // Milestone APIs
  getMilestones: getBugzillaMilestones_API,
  
  // Users
  getCurrentUser: getCurrentUser_API,
  getUsers: getBugzillaUsers_API,
  
  // Utilities
  testConnection: testBugzillaConnection_API,
  
  // Debug
  getDebugInfo: getDebugInfo_API,
  getDebugProducts: getDebugProducts_API,
  getDebugComponents: getDebugComponents_API,
};

export default BugzillaProxyController;